import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ColumnFilter } from "@/components/Portal/components/PortalDeviceListFilter/ColumnFilter/ColumnFilter";
import { Settings } from "@/components/Portal/components/PortalDeviceListFilter/Settings/Settings";
import { SimTypeFilter } from "@/components/Portal/components/PortalDeviceListFilter/SimTypeFilter/SimTypeFilter";
import { StatusChips } from "@/components/Portal/components/PortalDeviceListFilter/StatusChips/StatusChips";
import { FilterDialogForm } from "@/components/Portal/components/PortalDeviceListFilter/types";
import { useIsMobile } from "@/hooks/useIsMobile";

interface FilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onReset: () => void;
  isListView: boolean | null;
  watch: UseFormWatch<FilterDialogForm>;
  setValue: UseFormSetValue<FilterDialogForm>;
  register: UseFormRegister<FilterDialogForm>;
  control: Control<FilterDialogForm>;
  /** Whether any filters are active in the current dialog form (enables the apply button). */
  hasFormFields: boolean;
  /** Whether any filters are currently applied in the URL (shows the reset button). */
  hasAppliedFilters: boolean;
}

/** Section heading with a hairline divider beneath it. */
const FilterSection = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <div className="flex flex-col gap-1">
      <h3 className="text-body-base font-semibold leading-none">{label}</h3>
      <hr className="border-border-tertiary-inverse" />
    </div>
    {children}
  </div>
);

/** A modal dialog that contains the filtering options for the device list. */
export const FilterDialog = ({
  isOpen,
  onClose,
  onReset,
  isListView,
  watch,
  setValue,
  register,
  control,
  hasFormFields,
  hasAppliedFilters,
}: FilterDialogProps) => {
  const { t } = useTranslation("portal");
  const isMobile = useIsMobile();

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={t("cockpit.filter.title")}
      onClose={onClose}
      variant="dark"
      hasScroll={false}
      classNameDescription="flex flex-col gap-8"
    >
      <FilterSection label={t("common.device.attributes.status")}>
        <StatusChips watch={watch} setValue={setValue} register={register} />
      </FilterSection>

      <FilterSection label={t("cockpit.filter.activity")}>
        <Settings watch={watch} setValue={setValue} />
      </FilterSection>

      <FilterSection label={t("cockpit.filter.application")}>
        <SimTypeFilter control={control} />
      </FilterSection>

      {isListView && (
        <FilterSection label={t("cockpit.filter.additionalColumns")}>
          <ColumnFilter control={control} />
        </FilterSection>
      )}

      <div className="flex flex-col-reverse gap-2 tablet:flex-row tablet:justify-end tablet:gap-4">
        {hasAppliedFilters && (
          <Button
            variant="tertiary"
            inverted
            type="button"
            fullWidth={isMobile}
            onClick={onReset}
          >
            {t("cockpit.filter.resetFilters")}
          </Button>
        )}
        <Button
          variant="primary"
          inverted
          fullWidth={isMobile}
          disabled={!hasFormFields}
          onClick={onClose}
        >
          {t("cockpit.filter.apply")}
        </Button>
      </div>
    </Modal>
  );
};
